/**
 * RevenueCat anonymous "buy before sign-up" checkout.
 *
 * Preferred flow:
 *  1. Visitor clicks Buy → send them into RevenueCat + Stripe Billing.
 *  2. Stripe Checkout collects card / Apple Pay details.
 *  3. After payment, the buyer lands in the app portal with the anonymous
 *     RevenueCat app user ID so the new account can be linked to the purchase.
 *
 * RevenueCat supports two Stripe-backed engines here:
 *  - `strp_...` Stripe Billing keys: Stripe owns products/customers/checkout.
 *  - `rcb_...` Web Billing keys: RevenueCat owns checkout UI, Stripe is only the gateway.
 */
import { Purchases, type Package, type Offerings } from '@revenuecat/purchases-js'

// Stripe Billing is the only supported web checkout for AgentWhisper.
// This is a RevenueCat *public* Stripe Billing key. Keep the environment override
// so non-production environments can use their own key, but retain the production
// fallback: Vite variables are compiled during Netlify's build and a missing value
// previously made every landing-page CTA silently redirect to the login portal.
const PRODUCTION_STRIPE_BILLING_KEY = 'strp_QgKGIfofhkDDkAlacSevfnWHQfC'
const STRIPE_BILLING_KEY = (import.meta.env.VITE_REVENUECAT_STRIPE_KEY as string | undefined)
  ?? PRODUCTION_STRIPE_BILLING_KEY
const RC_KEY = STRIPE_BILLING_KEY
const PORTAL_URL = (import.meta.env.VITE_APP_PORTAL_URL as string | undefined)
  ?? 'https://app.agentwhisper.altuzar.dev'
const STRIPE_CHECKOUT_ENDPOINT = import.meta.env.VITE_STRIPE_CHECKOUT_ENDPOINT as string | undefined

/** Offering used for the launch promo; falls back to the current offering. */
const LAUNCH_OFFERING_ID = (import.meta.env.VITE_RC_LAUNCH_OFFERING as string | undefined) ?? 'launch'

/** Entitlement granted by the lifetime license products. */
export const ENTITLEMENT_ID = (import.meta.env.VITE_RC_ENTITLEMENT as string | undefined) ?? 'lifetime'

const ANON_ID_STORAGE_KEY = 'aw_rc_anonymous_id'

export const RETAIL_PRICE = '$79'
export const LAUNCH_PRICE = '$19.99'
export const LAUNCH_PURCHASE_CAP = 'launch price'
export const LAUNCH_PURCHASES_DONE = 'save $59.01'
export const LAUNCH_PURCHASES_LEFT = '75% off'
export const LAUNCH_CTA_LABEL = `Get AgentWhisper for ${LAUNCH_PRICE}, normally ${RETAIL_PRICE}`
export const LAUNCH_CTA_URGENCY = 'Launch price · Save $59.01 (75%)'

let purchases: Purchases | null = null

function getOrCreateAnonymousId(): string {
  try {
    const existing = localStorage.getItem(ANON_ID_STORAGE_KEY)
    if (existing) return existing
    const fresh = Purchases.generateRevenueCatAnonymousAppUserId()
    localStorage.setItem(ANON_ID_STORAGE_KEY, fresh)
    return fresh
  } catch {
    // localStorage unavailable (private mode), so a session-scoped ID still works
    return Purchases.generateRevenueCatAnonymousAppUserId()
  }
}

export function isCheckoutConfigured(): boolean {
  return Boolean(STRIPE_CHECKOUT_ENDPOINT || STRIPE_BILLING_KEY)
}

export function getCheckoutProviderLabel(): string {
  return 'Stripe Checkout'
}

function getPurchases(): Purchases {
  if (!RC_KEY) {
    throw new Error('VITE_REVENUECAT_STRIPE_KEY is not set')
  }
  if (!purchases) {
    purchases = Purchases.configure({ apiKey: RC_KEY, appUserId: getOrCreateAnonymousId() })
  }
  return purchases
}

export interface LaunchPackage {
  pkg: Package
  /** e.g. "$19.99", live price from RevenueCat */
  price: string
  currency: string
}

/** Pick the launch offering's lifetime package, falling back to current. */
export async function getLaunchPackage(): Promise<LaunchPackage | null> {
  const offerings: Offerings = await getPurchases().getOfferings()
  const offering = offerings.all[LAUNCH_OFFERING_ID] ?? offerings.current
  const pkg = offering?.availablePackages[0]
  if (!pkg) return null
  const price = pkg.webBillingProduct.currentPrice
  return {
    pkg,
    price: price.formattedPrice,
    currency: price.currency,
  }
}

export interface PurchaseOutcome {
  /** True when the entitlement is now active. */
  entitled: boolean
  /** URL of the app portal pre-wired to link this purchase to a new account. */
  portalUrl: string
}

/**
 * Open the configured launch checkout.
 *
 * Stripe Billing checkout (`strp_...`) via the RevenueCat Web SDK.
 */
export async function openStripeCheckout(discountCode?: string): Promise<PurchaseOutcome> {
  if (STRIPE_CHECKOUT_ENDPOINT) {
    const response = await fetch(STRIPE_CHECKOUT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appUserId: getOrCreateAnonymousId(),
        // Gift links intentionally open Stripe's promo-code field. Stripe does
        // not allow that field and a pre-applied launch discount in one session.
        gift: Boolean(discountCode),
      }),
    })
    const body = await response.json() as { url?: string; error?: string }
    if (!response.ok || !body.url) {
      throw new Error(body.error ?? 'Stripe Checkout could not be started')
    }
    window.location.assign(body.url)
    return await new Promise<PurchaseOutcome>(() => {})
  }

  const launch = await getLaunchPackage()
  if (!launch) {
    throw new Error('No package available. Check your RevenueCat offering configuration')
  }

  const { customerInfo } = await getPurchases().purchase({
    rcPackage: launch.pkg,
    // Web Billing supports discount-code UI. Stripe Billing ignores these fields
    // today, so Stripe coupons should be configured on the Stripe/RevenueCat side.
    ...(discountCode ? { discountCode } : {}),
    showDiscountCodeField: true,
  } as Parameters<Purchases['purchase']>[0])

  const entitled = ENTITLEMENT_ID in customerInfo.entitlements.active
  const anonymousId = getOrCreateAnonymousId()
  const portalUrl = `${PORTAL_URL}/?purchased=1&rcid=${encodeURIComponent(anonymousId)}`
  return { entitled, portalUrl }
}

/** Backwards-compatible name used by the CTA component. */
export const buyLifetime = openStripeCheckout

/** Returns true if this browser already bought a license anonymously. */
export async function hasExistingPurchase(): Promise<boolean> {
  if (!RC_KEY) return false
  try {
    const info = await getPurchases().getCustomerInfo()
    return ENTITLEMENT_ID in info.entitlements.active
  } catch {
    return false
  }
}

export function getPortalUrl(): string {
  let url = PORTAL_URL
  try {
    const anon = localStorage.getItem(ANON_ID_STORAGE_KEY)
    if (anon) url = `${PORTAL_URL}/?rcid=${encodeURIComponent(anon)}`
  } catch {
    /* ignore */
  }
  return url
}
